/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  EuiDescribedFormGroup,
  EuiSpacer,
  EuiLink,
  EuiInMemoryTable,
  EuiSkeletonText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { DocLink } from '@modules/doclinks'
import { networkSecurityUrl } from '@modules/network-security-pages/urls'
import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { useListTrafficFilterRulesetsQuery } from '@modules/cloud-lib/networkSecurity/hooks'
import { useGetDeploymentQuery } from '@modules/cloud-lib/deployments/hooks'

import { nameColumn, providerColumn, regionColumn, typeColumn } from '../TrafficFilterList/columns'
import TrafficFilterAssociationForm from '../TrafficFilterAssociationForm'

import ApplyButton from './ApplyButton'
import RemoveButton from './RemoveButton'

const DeploymentNetworkSecurity: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { deploymentId } = useParams()

  const { data: deployment } = useGetDeploymentQuery(deploymentId, {
    enabled: Boolean(deploymentId),
  })

  const [applyingRulesetType, setApplyingRulesetType] = useState<
    'ipFilter' | 'privateConnection' | null
  >(null)
  const { data, isLoading } = useListTrafficFilterRulesetsQuery()

  if (!deployment) {
    return <EuiSkeletonText lines={3} />
  }

  const filteredData = (data?.rulesets ?? []).filter(({ associations }) =>
    associations?.some(
      ({ entity_type, id }) => entity_type === 'deployment' && id === deploymentId,
    ),
  )

  const columns: Array<EuiBasicTableColumn<TrafficFilterRulesetInfo> | any> = [
    nameColumn,
    typeColumn,
    providerColumn,
    regionColumn,
    {
      name: (
        <FormattedMessage
          id='deploymentNetworkSecurity.list.ruleset.actions.header'
          defaultMessage='Actions'
        />
      ),
      align: 'center',
      render: (ruleset: TrafficFilterRulesetInfo) => (
        <RemoveButton deploymentId={deploymentId} ruleset={ruleset} />
      ),
    },
  ]

  return (
    <Fragment>
      <EuiDescribedFormGroup
        ratio='third'
        fullWidth={true}
        title={
          <h3>
            <FormattedMessage
              id='deploymentNetworkSecurity.title'
              defaultMessage='Network security'
            />
          </h3>
        }
        description={
          <div>
            <FormattedMessage
              id='deploymentNetworkSecurity.description-line-1'
              defaultMessage='Protect your deployment by applying IP filter or private connection policies. Traffic matching any applied policy is allowed, and all other traffic is denied. <docLink>Learn more</docLink>'
              values={{
                docLink: (content) => (
                  <DocLink link='configureDeploymenTrafficFilters'>{content}</DocLink>
                ),
              }}
            />

            <EuiSpacer size='m' />

            <FormattedMessage
              id='deploymentNetworkSecurity.description-line-2'
              defaultMessage="New policies can be created in your organization's <link>Network security settings</link>."
              values={{
                link: (content) => (
                  <EuiLink
                    href={networkSecurityUrl()}
                    onClick={(e) => {
                      e.preventDefault()
                      navigate(networkSecurityUrl())
                    }}
                  >
                    {content}
                  </EuiLink>
                ),
              }}
            />
          </div>
        }
      >
        <ApplyButton
          onClick={(rulesetType) => {
            setApplyingRulesetType(rulesetType)
          }}
        />

        <EuiSpacer size='l' />

        <EuiInMemoryTable
          tableLayout='auto'
          items={filteredData}
          columns={columns}
          loading={isLoading}
        />
      </EuiDescribedFormGroup>

      {renderFlyout()}
    </Fragment>
  )

  function renderFlyout() {
    if (applyingRulesetType && deployment) {
      return (
        <TrafficFilterAssociationForm
          onClose={() => {
            setApplyingRulesetType(null)
          }}
          rulesetType={applyingRulesetType}
          deployment={deployment}
        />
      )
    }

    return null
  }
}

export default DeploymentNetworkSecurity
